import { createStyles, IconButton, makeStyles, Typography } from "@material-ui/core";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import React from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";
import { getAllPublishedModules } from "../../api/ModuleApi";
import { RequiredCourse } from "../../model/Course";
import { GetLocalizedName, ModuleIndex, ModuleStateTranslationKey, ModuleTypeTranslationKey } from "../../model/Module";
import { routesPaths } from "../../Routes";
import { GetTranslation } from "../../translations/GetTranslation";
import { ColumnDefinition, CustomGrid } from "../common/customGrid/CustomGrid";
import TranslatedMessage from "../translations/TranslatedMessage";
import ModuleSelectionDialog from "./ModuleSelectionDialog";

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      marginBottom: "-3rem",
    },
  })
);

type CourseFormModulesProps = {
  courseModules: ModuleIndex[];
  handleAddModules: (modules: ModuleIndex[]) => void;
  handleRemoveModule: (module: ModuleIndex) => void;
  isReadOnly: boolean;
  requiredFieldStatus: RequiredCourse;
};

const CourseFormModules = (props: CourseFormModulesProps) => {
  const { courseModules, handleAddModules, handleRemoveModule, isReadOnly, requiredFieldStatus } = props;

  const [selectionDialogOpened, setSelectionDialogOpened] = React.useState(false);
  const [allModules, setAllModules] = React.useState<ModuleIndex[]>([]);

  const intl = useIntl();
  const classes = useStyles();
  const history = useHistory();

  React.useEffect(() => {
    let unmounted = false;
    getAllPublishedModules().then((modules) => {
      if (!unmounted) {
        setAllModules(modules);
      }
    });
    return () => {
      unmounted = true;
    };
  }, []);

  const columnsDefinitions: ColumnDefinition<ModuleIndex>[] = [
    {
      columnKey: "moduleName",
      columnLabel: <TranslatedMessage id="module.field.moduleName" />,
      cellValue: (_) => GetLocalizedName(_),
    },
    {
      columnKey: "moduleType",
      columnLabel: <TranslatedMessage id="module.field.moduleType" />,
      cellValue: (_) => (_.moduleType ? GetTranslation(intl, ModuleTypeTranslationKey[_.moduleType]) : ""),
    },
    {
      columnKey: "status",
      columnLabel: <TranslatedMessage id="common.field.status" />,
      cellValue: (_) => GetTranslation(intl, ModuleStateTranslationKey[_.status]),
    },
  ];

  if (!isReadOnly) {
    columnsDefinitions.push({
      columnKey: "removeModule",
      columnLabel: "",
      cellValue: (_) => (
        <IconButton
          color="secondary"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            handleRemoveModule(_);
          }}
        >
          <HighlightOffIcon />
        </IconButton>
      ),
      isSortingDisabled: true,
    });
  }

  const handleAcceptSelection = (modules: ModuleIndex[]) => {
    setSelectionDialogOpened(false);
    if (modules && modules.length) {
      handleAddModules(modules);
    }
  };

  return (
    <React.Fragment>
      <div className={isReadOnly ? "" : classes.header}>
        <Typography variant="h6">
          <TranslatedMessage id="course.section.modules" />
        </Typography>
        {requiredFieldStatus.isModuleListEmpty && (
          <Typography variant="body2" color="error">
            <TranslatedMessage id="course.validation.modules.empty" />
          </Typography>
        )}
      </div>
      <CustomGrid<ModuleIndex>
        columnDefinitions={columnsDefinitions}
        rows={courseModules}
        rowKey={(_) => _.id}
        addButtonTooltip={<TranslatedMessage id="course.button.addModules" />}
        onRowClick={(row: ModuleIndex) => history.push(routesPaths.module(row.id))}
        onAddClick={() => setSelectionDialogOpened(true)}
        hideSearchBar={true}
        hideAddButton={isReadOnly}
      />

      <ModuleSelectionDialog
        handleAccept={handleAcceptSelection}
        handleCancel={() => setSelectionDialogOpened(false)}
        isOpened={selectionDialogOpened}
        selectableModules={allModules.filter((module) => courseModules.every((cm) => cm.id !== module.id))}
      />
    </React.Fragment>
  );
};

export default CourseFormModules;
